import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T> {
  isSuccess: boolean;
  code: number;
  message: string;
  data: T;
  errors: string[];
}

export interface MailboxProvider {
  id: number;
  name: string;
  providerType: string;
  isEnabled: boolean;
  displayOrder: number;
  configurationSchemaJson: string;
  iconUrl: string | null;
  description: string | null;
}

export interface MailboxConnection {
  id: number;
  providerId: number;
  providerName: string;
  emailAddress: string;
  displayName: string | null;
  status: 'Connected' | 'Disconnected' | 'Error';
  lastSyncAt: string | null;
  connectedAt: string;
  isActive: boolean;
}

export interface CreateMailboxConnectionRequest {
  providerId: number;
  emailAddress: string;
  displayName?: string;
  configurationJson: string;
}

export interface UpdateMailboxConnectionRequest {
  id: number;
  displayName?: string;
  configurationJson?: string;
  isActive?: boolean;
}

export interface MailboxSyncStatus {
  totalCount: number;
  syncedCount: number;
  availableCount: number;
}

export interface MailboxSyncResult {
  batchId: string | null;
  syncedMessageCount: number;
  failedMessageCount: number;
  discoveredMessageCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class MailboxService {
  private readonly endpoint = `${environment.apiUrl}/Mailbox`;

  constructor(private http: HttpClient) {}

  getProviders(): Observable<ApiResponse<MailboxProvider[]>> {
    return this.http.get<ApiResponse<MailboxProvider[]>>(`${this.endpoint}/Providers`);
  }

  getConnections(): Observable<ApiResponse<MailboxConnection[]>> {
    return this.http.get<ApiResponse<MailboxConnection[]>>(`${this.endpoint}/Connections`);
  }

  createConnection(request: CreateMailboxConnectionRequest): Observable<ApiResponse<MailboxConnection>> {
    return this.http.post<ApiResponse<MailboxConnection>>(`${this.endpoint}/Connections`, request);
  }

  updateConnection(request: UpdateMailboxConnectionRequest): Observable<ApiResponse<MailboxConnection>> {
    return this.http.put<ApiResponse<MailboxConnection>>(`${this.endpoint}/Connections/${request.id}`, request);
  }

  deleteConnection(id: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.endpoint}/Connections/${id}`);
  }

  testConnection(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.endpoint}/Connections/${id}/Test`, {});
  }

  getZohoAuthorizeUrl(): Observable<{ authorizationUrl: string }> {
    return this.http.get<{ authorizationUrl: string }>(`${this.endpoint}/Zoho/authorize`);
  }

  getOutlookAuthorizeUrl(): Observable<{ authorizationUrl: string }> {
    const returnUrl = encodeURIComponent(window.location.origin);
    return this.http.get<{ authorizationUrl: string }>(`${this.endpoint}/Outlook/authorize?returnUrl=${returnUrl}`);
  }

  getMessages(connectionId: number, folderId?: string, page: number = 1, limit: number = 20): Observable<ApiResponse<any[]>> {
    let url = `${this.endpoint}/messages?connectionId=${connectionId}&page=${page}&limit=${limit}`;
    if (folderId) {
      url += `&folderId=${folderId}`;
    }
    return this.http.get<ApiResponse<any[]>>(url);
  }

  getSyncStatus(connectionId: number): Observable<ApiResponse<MailboxSyncStatus>> {
    return this.http.get<ApiResponse<MailboxSyncStatus>>(`${this.endpoint}/sync-status?connectionId=${connectionId}`);
  }

  syncMailbox(connectionId: number): Observable<ApiResponse<MailboxSyncResult>> {
    return this.http.post<ApiResponse<MailboxSyncResult>>(`${this.endpoint}/sync?connectionId=${connectionId}`, {});
  }

  clearMailbox(connectionId: number): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.endpoint}/ClearAllEmails?connectionId=${connectionId}`, {});
  }
}